import React, { useEffect } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import {
  GetEventDocument,
  useGetEventQuery,
} from '../../../graphql/queries/getEvent.generated';
import i18n from '@/utils/i18n';
import { EventLinkedUsersTable } from './EventLinkedUsersTable';
import OrderFulfillmentsTableWithFilter from '../components/OrderFulfillmentsTableWithFilter';
import { useUpdateEventMutation } from '../../../graphql/mutations/updateEvent.generated';
import { Communicator } from '../../../tools/Communication';
import { ContainerType } from '../OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import { FaTrash } from 'react-icons/fa';
import EditEventForm from '../components/EditEventForm';
import { useDeleteEventMutation } from '../../../graphql/mutations/deleteEvent.generated';
import { LayoutStoreInstance } from '../../../LayoutStore';
import EventInvoiceInfoTab from './EventInvoiceInfoTab';
import EventAllowedTypesTab from './EventAllowedTypesTab';
import type {
  AllowedEventContainerTypes,
  AllowedEventItemTypes,
} from './types';
import EventPriceTable, {
  DefaultContainerPriceMap,
  EventPriceType,
  StringifiedEventPrice,
} from '../components/EventPricesTable';
import {
  EventPriceTarget,
  isSameTarget,
  priceTarget,
} from '../components/eventPriceTarget';
import { StoreDefaultContainerPrice } from '../../ContainerTypes/ContainerPriceTable';
import { ZohoEventButton } from '../../../components/Button/ZohoEventButton';
import { EventAccount, EventType } from '../../../graphql/generatedTypes';
import EventInvoicingTab from './EventInvoicingTab';
import { GetEventInvoicingReadinessDocument } from '../../../graphql/queries/getEventInvoicingReadiness.generated';
import { useGetAllEventLinkedUsersQuery } from '../../../graphql/queries/getAllEventLinkedUsers.generated';
import ParticipatingEventAccountsList from './components/ParticipatingEventAccountsList';
import DownloadsModal from '../components/DownloadsModal';
import {
  ContextSidebarConfig,
  SidebarLink,
  useRouteSidebar,
} from '@/components/SidebarContext';
import { Routes } from '@/utils/routes';
import { EventStockOverview } from './EventStockOverview';
import { EventStockOverviewV2 } from './EventStockOverviewV2';
import { EventFulfillmentData } from './EventFulfilmentData';
import { BootstrapRoute } from '@/utils/BootstrapRoute';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EventOrdersTabNew } from './EventOrdersTab';
import { isDev, isLocalDev } from '@/utils/isDev';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import EditEventMerchantOrderPage from '@/views/Events/EventOrder/EditEventMerchantOrder/EditEventMerchantOrderPage';
import { EventDeliveryAddressesTab } from './EventDeliveryAddressesTab';
import { EventItemLocationsTab } from './EventItemLocationsTab';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import { EventOrderDetails } from '@/views/Order/EventOrderDetails';
import { EventOrderForms } from './EventOrderForms';
import EventOrderFulfillmentDataTab from './EventOrderFulfillmentDataTab';
import { EventOrdersTab_OLD } from './EventOrdersTab_OLD';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';

export const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const isGeneralInfo = useRouteMatch(
    Routes.eventDetailGeneralInfo(':eventId'),
  );

  const shouldUseNewOrderPages = useFeatureFlag('feature_new_order_management');

  // this is just to test editing in the af for now.
  // will be read only after integration with zoho
  const [allowedContainerTypes, setAllowedContainerTypes] = React.useState<
    AllowedEventContainerTypes[]
  >([]);
  const [allowedItemTypes, setAllowedItemTypes] = React.useState<
    AllowedEventItemTypes[]
  >([]);
  const [currency, setCurrency] = React.useState('');
  const [allContainerTypes, setAllContainerTypes] = React.useState<
    ContainerType[]
  >([]);
  const [type, setType] = React.useState<EventType>(EventType.Default);
  const [showRemoveModal, setShowRemoveModal] = React.useState(false);

  const [prices, setPrices] = React.useState<StringifiedEventPrice[]>([]);
  const [storeDefaultContainerPrices, setStoreDefaultContainerPrices] =
    React.useState<StoreDefaultContainerPrice[]>([]);
  const {
    loading: queryingEvent,
    error,
    data,
  } = useGetEventQuery({
    variables: { id: eventId },
    fetchPolicy: 'cache-and-network', // Force fresh data from network
  });

  useDocumentTitle('PageTitles:event', data?.event?.name);

  const sidebarConfig: ContextSidebarConfig = {
    mode: 'grouped',
    back: {
      label: i18n.t('Event:backToAllEvents'),
      to: Routes.events(),
    },
    groups: [
      {
        label: i18n.t('Events:sidebarSetup'),
        links: [
          {
            label: i18n.t('Event:generalInfo'),
            href: Routes.eventDetailGeneralInfo(eventId),
            iconName: 'calendar-star-event',
          },
          {
            label: i18n.t('Event:prices'),
            href: Routes.eventDetailPrices(eventId),
            iconName: 'container-bowl-cup-stock',
          },
          {
            label: i18n.t('Event:allowedTypes'),
            href: Routes.eventDetailAllowedTypes(eventId),
            iconName: 'list',
          },
          {
            label: i18n.t('Event:eventAccounts'),
            href: Routes.eventDetailEventAccounts(eventId),
            iconName: 'person-circle-account',
          },
          shouldUseNewOrderPages && {
            label: i18n.t('Event:itemLocations'),
            href: Routes.eventDetailItemLocations(eventId),
            iconName: 'location-pin',
          },
          {
            label: i18n.t('Event:invoiceInfo'),
            href: Routes.eventDetailInvoiceInfo(eventId),
            iconName: 'invoice-receipt-bill',
          },
          {
            label: i18n.t('Event:linkedUsers'),
            href: Routes.eventDetailLinkedUsers(eventId),
            iconName: 'person',
          },
          {
            label: i18n.t('Common:downloads'),
            href: Routes.eventDetailDownloads(eventId),
            iconName: 'arrow-box-down-save',
          },
          shouldUseNewOrderPages && {
            label: i18n.t('Events:deliveryAddressesTitle'),
            href: Routes.eventDetailDeliveryAddresses(eventId),
            iconName: 'truck',
          },
          shouldUseNewOrderPages && {
            label: i18n.t('Events:sidebarOrderForms'),
            href: Routes.eventDetailOrderForms(eventId),
            iconName: 'document',
          },
        ].filter(Boolean) as SidebarLink[],
      },
      {
        label: i18n.t('Events:sidebarLiveData'),
        links: [
          {
            label: i18n.t('Event:stockOverview'),
            href: Routes.eventDetailStockOverview(eventId),
            iconName: 'container-bowl-cup-stock',
          },
          {
            label: i18n.t('Event:fulfillmentData'),
            href: Routes.eventDetailFulfillmentData(eventId),
            iconName: 'clipboard-container' as const,
          },
          ...(isDev() || isLocalDev()
            ? [
                {
                  label: i18n.t('Event:fulfillmentData') + ' (V2 - DEV ONLY)',
                  href: Routes.eventDetailFulfillmentDataV2(eventId),
                  iconName: 'clipboard-container' as const,
                },
              ]
            : []),
          {
            label: i18n.t('Event:eventOrders'),
            href: Routes.eventDetailOrders(eventId),
            iconName: 'box',
          },
          {
            label: i18n.t('Event:orderFulfillments'),
            href: Routes.eventDetailOrderFulfillments(eventId),
            iconName: 'invoice-receipt-bill',
          },
        ],
      },
      {
        label: i18n.t('Events:sidebarWrapUp'),
        links: [
          {
            label: i18n.t('Event:generateInvoices'),
            href: Routes.eventDetailInvoicing(eventId),
            iconName: 'document',
          },
        ],
      },
    ],
  };

  useRouteSidebar(sidebarConfig);
  useBreadcrumbs({
    id: 'event-detail',
    priority: 100,
    items: [
      {
        label:
          (data?.event?.name ?? i18n.t('Event:event')) +
          (data?.event?.is_deleted === true ? ` (DELETED)` : ''),
        href: Routes.eventDetail(eventId),
      },
    ],
    sidebarConfig,
  });

  const [updateEvent, { loading: updatingEvent }] = useUpdateEventMutation({
    refetchQueries: [GetEventDocument, GetEventInvoicingReadinessDocument],
    onCompleted: () => {
      LayoutStoreInstance.setSuccess(i18n.t('Event:savedEvent'));
    },
    onError: (error) => {
      LayoutStoreInstance.setError(i18n.t('Event:savedEventError') + error);
    },
  });

  const [deleteEvent] = useDeleteEventMutation();

  const {
    loading: loadingEventLinkedUser,
    error: errorEventLinkedUser,
    data: dataEventLinkedUser,
    refetch: refetchEventLinkedUser,
  } = useGetAllEventLinkedUsersQuery({
    variables: { filter: { event_id: eventId } },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setAllContainerTypes(data);
      });
    Communicator.Get('/Config/GetConfigValue', {
      key: 'DefaultContainerInformation',
    })
      .then((response) => response.json())
      .then((data: StoreDefaultContainerPrice[]) => {
        setStoreDefaultContainerPrices(data);
      });
  }, []);

  const defaultContainerPrices = React.useMemo<DefaultContainerPriceMap>(() => {
    const map: DefaultContainerPriceMap = {};
    if (!currency) return map;
    const currencyKey = currency.toUpperCase();
    for (const entry of storeDefaultContainerPrices) {
      const row = entry[currencyKey];
      if (!row) continue;
      map[entry.Id] = {
        rental_price: row.RentalPrice,
        cleaning_price: row.CleaningPrice,
        deposit_price: row.DepositPrice,
        lost_price: row.LostPrice,
        logistic_partner_cleaning_price: row.LogisticPartnerCleaningPrice,
      };
    }
    return map;
  }, [storeDefaultContainerPrices, currency]);

  useEffect(() => {
    if (data?.event) {
      setAllowedContainerTypes(data.event.allowed_container_types);
      setAllowedItemTypes(data.event.allowed_item_types);
      setCurrency(data.event.currency);
      setPrices(data.event.prices as any);
      setType(data.event.type);
    }
  }, [data]);

  function setPrice(
    priceType: EventPriceType,
    target: EventPriceTarget,
    value: string,
  ): void {
    setPrices((prevPrices) => {
      const newPrices = prevPrices.map((price) => {
        if (isSameTarget(priceTarget(price), target)) {
          return {
            ...price,
            // Prices are whole cents, so a decimal point has no meaning here. Letting
            // one through cost a factor of 100: emptyPriceToNull's parseInt read 2.50 as 2.
            [priceType]: value.replace(/[^0-9]/g, ''),
          };
        }
        return price;
      });
      return newPrices;
    });
  }

  async function saveEventPrices() {
    await updateEvent({
      variables: {
        event_id: eventId,
        input: {
          prices: prices.map((price) => ({
            container_type_id: price.container_type_id,
            item_type_id: price.item_type_id,
            rental_price: emptyPriceToNull(price.rental_price),
            cleaning_price: emptyPriceToNull(price.cleaning_price),
            deposit_price: emptyPriceToNull(price.deposit_price),
            lost_price: emptyPriceToNull(price.lost_price),
            logistic_partner_cleaning_price: emptyPriceToNull(
              price.logistic_partner_cleaning_price,
            ),
          })),
        },
      },
    });
  }

  async function saveEventCurrency() {
    if (
      externalZohoId ||
      currency == null ||
      data?.event?.currency === currency
    )
      return;
    await updateEvent({
      variables: {
        event_id: eventId,
        input: { currency },
      },
    });
  }

  async function saveEventAllowedTypes() {
    await updateEvent({
      variables: {
        event_id: eventId,
        input: {
          allowed_container_type_ids: allowedContainerTypes.map((ct) => ct.id),
          allowed_item_type_ids: allowedItemTypes.map((it) => it.id),
        },
      },
    });
  }

  if (queryingEvent) return <p>{i18n.t('Common:loading')}</p>;
  if (error) return <p>{i18n.t('Common:error')}</p>;

  if (!data?.event) return <p>{i18n.t('Event:noEventFound')}</p>;

  const {
    name,
    description,
    external_zoho_id: externalZohoId,
    event_participants: eventParticipants,
    entity,
    is_deleted: isDeleted,
  } = data.event;

  const eventLinkedUsers = dataEventLinkedUser?.getAllEventLinkedUsers || [];

  return (
    <div className="tw:container tw:flex tw:flex-col tw:gap-6 tw:pb-8">
      <Switch>
        <Route exact path={Routes.eventDetail(':eventId')}>
          <Redirect to={Routes.eventDetailGeneralInfo(eventId)} />
        </Route>
        <Route path={Routes.eventDetailGeneralInfo(':eventId')}>
          <div className="tw:flex tw:items-center tw:justify-end tw:gap-4">
            {externalZohoId && (
              <ZohoEventButton externalZohoId={externalZohoId} />
            )}
            {isGeneralInfo && !isDeleted && !externalZohoId && (
              <button
                onClick={() => setShowRemoveModal(true)}
                className="tw:inline-flex tw:items-center tw:px-4 tw:py-2 tw:bg-red-500 tw:text-white tw:rounded tw:hover:bg-red-600 tw:transition-colors"
              >
                <FaTrash className="tw:mr-2" /> {i18n.t('Common:delete')}
              </button>
            )}
          </div>
          <EditEventForm eventId={eventId} />
        </Route>
        <Route path={Routes.eventDetailInvoiceInfo(':eventId')}>
          <EventInvoiceInfoTab
            currency={currency}
            entity={entity}
            loading={updatingEvent || queryingEvent}
            saveEvent={saveEventCurrency}
            setCurrency={setCurrency}
            external_zoho_id={externalZohoId}
          />
        </Route>
        <Route path={Routes.eventDetailAllowedTypes(':eventId')}>
          <EventAllowedTypesTab
            allContainerTypes={allContainerTypes}
            allowedContainerTypes={allowedContainerTypes}
            setAllowedContainerTypes={setAllowedContainerTypes}
            allowedItemTypes={allowedItemTypes}
            setAllowedItemTypes={setAllowedItemTypes}
            loading={
              updatingEvent || queryingEvent || allContainerTypes.length === 0
            }
            saveEvent={saveEventAllowedTypes}
          />
        </Route>
        <Route path={Routes.eventDetailPrices(':eventId')}>
          <EventPriceTable
            allContainerTypes={allContainerTypes}
            allItemTypes={allowedItemTypes}
            currency={currency}
            error={error}
            loading={updatingEvent || queryingEvent}
            prices={prices}
            savePrices={saveEventPrices}
            setPrice={setPrice}
            showDefaultColumn={false}
            defaultContainerPrices={defaultContainerPrices}
          />
        </Route>
        <Route path={Routes.eventDetailInvoicing(':eventId')}>
          <EventInvoicingTab
            eventId={eventId}
            eventParticipants={eventParticipants || []}
            eventType={type}
            mainContractingEventAccount={
              data.event.main_contracting_event_account
            }
          />
        </Route>
        <BootstrapRoute path={Routes.eventDetailOrderFulfillments(':eventId')}>
          <OrderFulfillmentsTableWithFilter
            filter={{ event_id: eventId }}
            authorizedSigners={[]}
            events={[{ id: eventId, name: name || '' }]}
            itemLocations={[]}
            eventAccounts={
              eventParticipants?.map((ea) => ({
                id: ea.id,
                name: ea.name || '',
              })) || []
            }
          />
        </BootstrapRoute>
        <Route path={Routes.eventDetailEventAccounts(':eventId')}>
          <ParticipatingEventAccountsList
            eventParticipants={eventParticipants as EventAccount[]}
            eventId={eventId}
            eventName={name}
            mainContractingEventAccount={
              data.event.main_contracting_event_account
            }
            allowedContainerTypes={allowedContainerTypes}
          />
        </Route>
        <Route path={Routes.eventDetailLinkedUsers(':eventId')}>
          <EventLinkedUsersTable
            eventId={eventId}
            eventEventLinkedUsers={eventLinkedUsers}
            errorEventLinkedUsers={errorEventLinkedUser}
            loadingEventLinkedUsers={loadingEventLinkedUser}
            refetchEventLinkedUser={refetchEventLinkedUser}
          />
        </Route>
        <BootstrapRoute path={Routes.eventDetailDownloads(':eventId')}>
          <DownloadsModal
            event={data.event}
            showDeposit={false}
            showInvoice
            showOrderfulfillment
            showEventOpsOrders
            showAllEventOpsOrders
            showAuthorizedSigner
          />
        </BootstrapRoute>
        <Route path={Routes.eventDetailDeliveryAddresses(':eventId')}>
          <EventDeliveryAddressesTab eventId={eventId} />
        </Route>
        <Route path={Routes.eventDetailOrderForms(':eventId')}>
          <EventOrderForms
            eventId={eventId}
            eventParticipants={eventParticipants ?? []}
            merchantOrderFormDeadline={data.event.merchant_order_form_deadline}
          />
        </Route>
        <Route path={Routes.eventDetailItemLocations(':eventId')}>
          <EventItemLocationsTab
            eventParticipants={data.event.event_participants}
          />
        </Route>
        <Route
          path={Routes.editEventMerchantOrderSharedLink(
            ':eventId',
            ':sharedLinkId',
          )}
        >
          <EditEventMerchantOrderPage />
        </Route>
        <Route path={Routes.eventDetailOrders(':eventId')}>
          <Switch>
            <Route exact path={Routes.eventDetailOrders(':eventId')}>
              {!shouldUseNewOrderPages && (
                <EventOrdersTab_OLD eventId={eventId} eventName={name} />
              )}
              {shouldUseNewOrderPages && (
                <EventOrdersTabNew
                  eventId={eventId}
                  eventName={name}
                  allowedContainerTypes={data.event.allowed_container_types.map(
                    (ct) => ({
                      id: ct.id,
                      name: ct.name,
                      imageUrl: ct.image,
                    }),
                  )}
                  eventParticipants={data.event.event_participants ?? []}
                />
              )}
            </Route>
            <Route
              path={Routes.eventOrderDetail(':eventId', ':orderId')}
              component={EventOrderDetails}
            />
          </Switch>
        </Route>

        <BootstrapRoute path={Routes.eventDetailStockOverview(':eventId')}>
          <EventStockOverviewV2 event={data.event} />
        </BootstrapRoute>
        <BootstrapRoute path={Routes.eventDetailStockOverviewOld(':eventId')}>
          <EventStockOverview event={data.event} />
        </BootstrapRoute>
        <Route path={Routes.eventDetailFulfillmentData(':eventId')}>
          <EventOrderFulfillmentDataTab
            eventId={eventId}
            eventType={type}
            eventParticipants={eventParticipants || []}
          />
        </Route>
        <Route path={Routes.eventDetailFulfillmentDataV2(':eventId')}>
          <EventFulfillmentData eventId={eventId} />
        </Route>
      </Switch>
      <DeleteEventModal
        isOpen={showRemoveModal}
        onOpenChange={setShowRemoveModal}
        onCancel={() => setShowRemoveModal(false)}
        onConfirm={() => {
          deleteEvent({
            variables: { event_id: eventId },
            onCompleted: () => {
              setShowRemoveModal(false);
              LayoutStoreInstance.setSuccess(i18n.t('Event:deleteEvent'));
            },
            onError: (error) => {
              setShowRemoveModal(false);
              LayoutStoreInstance.setError(
                i18n.t('Event:deleteEventError') + error.message,
              );
            },
          });
        }}
      >
        <p className="tw:text-sm tw:pb-4">{i18n.t('Event:deleteModalBody')}:</p>
        <div className="tw:space-y-2">
          <p className="tw:text-sm">
            <span className="tw:font-medium">{i18n.t('Common:name')}:</span>{' '}
            {name}
          </p>
          <p className="tw:text-sm">
            <span className="tw:font-medium">
              {i18n.t('Common:description')}:
            </span>{' '}
            {description}
          </p>
        </div>
      </DeleteEventModal>
    </div>
  );
};

function emptyPriceToNull(
  value: string | number | null | undefined,
): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function DeleteEventModal({
  isOpen,
  onOpenChange,
  onConfirm,
  onCancel,
  children,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="tw:sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{i18n.t('Event:deleteModalTitle')}</DialogTitle>
          <DialogDescription>{children}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose>
            <Button variant="outline" onClick={onCancel}>
              {i18n.t('Common:no')}
            </Button>
          </DialogClose>
          <Button variant="destructive" onClick={onConfirm}>
            {i18n.t('Common:yes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
